import React from 'react'
import {
  Button,
  Checkbox,
  MenuItem,
  Select,
  Divider,
  Slider,
  TextField,
  Switch,
} from '@material-ui/core'
import { useHistory } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { Typography } from '@material-ui/core'
import convert from 'color-convert'
import { Event } from 'three'

import {
  Q_MATERIAL_CATALOGUE,
  Q_MATERIAL_TYPES,
  Q_MATERIAL_CAT_SWATCH_ID,
} from 'graphql/query'
import { MC_MATERIAL_SWATCH } from 'graphql/mutations'
import Loading from 'components/Global/Loading'
import { catalogueType, matTypesType, swatchCreateType } from '../materialTypes'
import styles from '../materials.module.scss'

interface overrideType {
  albedoMap: boolean
  metalness: boolean
  roughness: boolean
  roughnessMap: boolean
  normals: boolean
  normalMap: boolean
}

const CreateSwatch = ({ close }: { close: () => void }) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const qCats = useQuery(Q_MATERIAL_CATALOGUE).refetch
  const qMatTypes = useQuery(Q_MATERIAL_TYPES).refetch
  const catSwId = useQuery(Q_MATERIAL_CAT_SWATCH_ID).refetch
  let history = useHistory()

  const [call] = useMutation(MC_MATERIAL_SWATCH)

  const [override, setOverride] = React.useState<overrideType>({
    albedoMap: false,
    metalness: false,
    roughness: false,
    roughnessMap: false,
    normals: false,
    normalMap: false,
  })

  const [swatchData, setSwatchData] = React.useState<swatchCreateType>({
    order: 0,
    name: '',
    l1: 0,
    l2: 0,
    l3: 0,
    a1: 0,
    a2: 0,
    a3: 0,
    b1: 0,
    b2: 0,
    b3: 0,
    c1: 0,
    c2: 0,
    c3: 0,
    h1: 0,
    h2: 0,
    h3: 0,
    catId: parseInt(history.location.pathname.split('/')[3]),
    code: '',
    albedoMap: undefined,
    detail: '',
    previews: [],
    highlight: false,
    metalness: 0,
    roughness: 0.3,
    roughnessMap: undefined,
    normalMap: undefined,
    normalSizeU: 10,
    normalSizeV: 10,
    normalStrength: 1,
    typeId: 0,
  })

  const [cats, setCats] = React.useState<catalogueType[]>()
  const [matTypes, setMatTypes] = React.useState<matTypesType[]>()
  const [avgLab, setAvgLab] = React.useState<string>('#000000')
  const [avgLch, setAvgLch] = React.useState<string>('#000000')
  const [matType, setMatType] = React.useState<{ [key: string]: any }>()

  const setCurType = (id: number) => {
    matTypes &&
      matTypes.forEach(t => {
        if (id == t.id) {
          setMatType(t)
        }
      })
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    let swatchDataTemp: { [key: string]: any } = { ...swatchData }
    let curOverride: { [key: string]: any } = { ...override }
    const curOverrideKeys = Object.keys(curOverride)

    if (matType !== undefined) {
      const checkOveride = (i: string) => {
        return (
          swatchDataTemp[i] === matType[i] ||
          curOverride[i] === false ||
          swatchDataTemp[i] === undefined
        )
      }
      curOverrideKeys.forEach(key => {
        switch (key) {
          case 'metalness': {
            if (checkOveride(key)) {
              delete swatchDataTemp[key]
            }
            break
          }
          case 'roughness': {
            if (checkOveride(key)) {
              delete swatchDataTemp[key]
            }
            break
          }
          case 'roughnessMap': {
            if (checkOveride(key)) {
              delete swatchDataTemp[key]
            }
            break
          }
          case 'albedoMap': {
            if (checkOveride(key)) {
              delete swatchDataTemp[key]
            }
            break
          }
          case 'normals': {
            if (curOverride[key] !== false) {
              swatchDataTemp.normalStrength === matType.normalStrength &&
                delete swatchDataTemp.normalStrength
              swatchDataTemp.normalSizeU === matType.normalSizeU &&
                delete swatchDataTemp.normalSizeU
              swatchDataTemp.normalSizeV === matType.normalSizeV &&
                delete swatchDataTemp.normalSizeV
            } else {
              delete swatchDataTemp.normalStrength
              delete swatchDataTemp.normalSizeU
              delete swatchDataTemp.normalSizeV
            }
            break
          }
          case 'normalMap': {
            if (checkOveride(key)) {
              delete swatchDataTemp[key]
            }
            break
          }
          default: {
            console.log(key, 'not handeled')
          }
        }
      })
      setLoading(true)
      catSwId() &&
        catSwId().then(res => {
          swatchDataTemp.order = res.data.rMaterialCatalogue[0].swatches.length
          console.log(swatchDataTemp)
          call({ variables: { ...swatchDataTemp } })
            .then(res => {
              console.log(res)
            })
            .catch(err => console.error(err))
            .finally(close)
        })
    }
  }

  React.useEffect(() => {
    qCats() &&
      qCats().then(res => {
        setCats(res.data.rMaterialCatalogue)
      })
    qMatTypes() &&
      qMatTypes().then(res => {
        setMatTypes(res.data.rMaterialType)
      })
  }, [])

  React.useEffect(() => {
    if (matType !== undefined) {
      setSwatchData({ ...swatchData, normalStrength: matType.normalStrength })
      setSwatchData({ ...swatchData, normalSizeU: matType.normalSizeU })
      setSwatchData({ ...swatchData, normalSizeV: matType.normalSizeV })
      setSwatchData({ ...swatchData, metalness: matType.metalness })
      setSwatchData({ ...swatchData, roughness: matType.roughness })
    }
  }, [matType])

  React.useEffect(() => {
    const avgL = (swatchData.l1 + swatchData.l2 + swatchData.l3) / 3
    const avgA = (swatchData.a1 + swatchData.a2 + swatchData.a3) / 3
    const avgB = (swatchData.b1 + swatchData.b2 + swatchData.b3) / 3
    const avgC = (swatchData.c1 + swatchData.c2 + swatchData.c3) / 3
    const avgH = (swatchData.h1 + swatchData.h2 + swatchData.h3) / 3

    setAvgLab('#' + convert.lab.hex([avgL, avgA, avgB]))
    setAvgLch('#' + convert.lch.hex([avgL, avgC, avgH]))
  }, [swatchData])

  return loading ? (
    <Loading />
  ) : (
    <form onSubmit={e => onSubmit(e)} className={styles.cSRoot}>
      <div className={styles.headBlock}>
        <TextField
          label="swatch name"
          value={swatchData.name}
          onChange={e => setSwatchData({ ...swatchData, name: e.target.value })}
          inputProps={{ maxLength: 40 }}
          className={styles.sNameTxtF}
        />{' '}
        <TextField
          label="Swatch Code"
          value={swatchData.code}
          onChange={e => setSwatchData({ ...swatchData, code: e.target.value })}
          inputProps={{ maxLength: 2 }}
          className={styles.marginP5}
        />
        <Select
          labelId="label"
          id="catalogue"
          value={swatchData.catId}
          onChange={(e: Event) => {
            setSwatchData({ ...swatchData, catId: parseInt(e.target.value) })
          }}
          className={styles.sel}
        >
          {cats !== undefined &&
            cats.map((cat, i) => (
              <MenuItem value={cat.id} key={i}>
                {cat.name}
              </MenuItem>
            ))}
        </Select>
        <Select
          labelId="label"
          id="material types"
          value={swatchData.typeId}
          onChange={(e: Event) => {
            setSwatchData({ ...swatchData, typeId: parseInt(e.target.value) })
            setCurType(e.target.value)
          }}
          className={styles.sel}
        >
          {matTypes !== undefined &&
            matTypes.map((type, i) => (
              <MenuItem value={type.id} key={i}>
                {type.name}
              </MenuItem>
            ))}
        </Select>
      </div>
      <div className={styles.headBlock}>
        <TextField
          label="Swatch Detail"
          value={swatchData.detail}
          onChange={e =>
            setSwatchData({ ...swatchData, detail: e.target.value })
          }
          inputProps={{ maxLength: 40 }}
          className={styles.sDetailTf}
        />
        <div className={styles.hghLitWrapper}>
          <Typography variant="h6">Highlight</Typography>
          <Switch
            checked={swatchData.highlight}
            color="secondary"
            onChange={e =>
              setSwatchData({
                ...swatchData,
                highlight: e.target.checked,
              })
            }
          />
        </div>
      </div>
      <Button
        id="Preview"
        variant="contained"
        component="label"
        fullWidth
        className={styles.marginP5}
      >
        "select Preview files 'png'"
        <input
          type="file"
          accept=".png"
          hidden
          multiple
          onChange={e => {
            if (e.target.files) {
              setSwatchData({ ...swatchData, previews: e.target.files })
            }
          }}
        />
      </Button>
      <Divider className={styles.margin} />
      <Typography variant="h6" className={styles.margin}>
        Color
      </Typography>
      <div className={styles.flex}>
        <div>
          <div className={styles.cSSection}>
            <TextField
              error={swatchData.l1 > 100}
              label="L"
              value={swatchData.l1}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  l1: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.a1 > 127 || swatchData.a1 < -128}
              label="A"
              value={swatchData.a1}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  a1: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.b1 > 127 || swatchData.b1 < -128}
              label="B"
              value={swatchData.b1}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  b1: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.c1 > 127 || swatchData.c1 < -128}
              label="C"
              value={swatchData.c1}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  c1: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.h1 > 127 || swatchData.h1 < -128}
              label="H"
              value={swatchData.h1}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  h1: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <Divider orientation="vertical" />{' '}
          </div>
          <div className={styles.cSSection}>
            <TextField
              error={swatchData.l2 > 100}
              label="L"
              value={swatchData.l2}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  l2: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.a2 > 127 || swatchData.a2 < -128}
              label="A"
              value={swatchData.a2}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  a2: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.b2 > 127 || swatchData.b2 < -128}
              label="B"
              value={swatchData.b2}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  b2: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.c2 > 127 || swatchData.c2 < -128}
              label="C"
              value={swatchData.c2}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  c2: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.h2 > 127 || swatchData.h2 < -128}
              label="H"
              value={swatchData.h2}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  h2: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <Divider orientation="vertical" />{' '}
          </div>
          <div className={styles.cSSection}>
            <TextField
              error={swatchData.l3 > 100}
              label="L"
              value={swatchData.l3}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  l3: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.a3 > 127 || swatchData.a3 < -128}
              label="A"
              value={swatchData.a3}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  a3: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.b3 > 127 || swatchData.b3 < -128}
              label="B"
              value={swatchData.b3}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  b3: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.c3 > 127 || swatchData.c3 < -128}
              label="C"
              value={swatchData.c3}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  c3: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <TextField
              error={swatchData.h3 > 127 || swatchData.h3 < -128}
              label="H"
              value={swatchData.h3}
              type="number"
              onChange={e => {
                setSwatchData({
                  ...swatchData,
                  h3: Number.isNaN(parseFloat(e.target.value))
                    ? 0
                    : parseFloat(e.target.value),
                })
              }}
              inputProps={{ maxLength: 4 }}
              className={styles.clrIndvWrapper}
            />
            <Divider orientation="vertical" />{' '}
          </div>
        </div>
        <Divider orientation="vertical" />

        <div className={styles.avgContainer}>
          <Typography>Average Lab</Typography>
          <div
            style={{
              background: `${avgLab}`,
            }}
            className={styles.avgColor}
          />
        </div>
        <div className={styles.avgContainer}>
          <Typography>Average Lch</Typography>
          <div
            style={{
              background: `${avgLch}`,
            }}
            className={styles.avgColor}
          />
        </div>
      </div>
      <Divider className={styles.margin} />
      <Typography variant="h6" className={styles.margin}>
        Color Map
      </Typography>
      <div className={styles.flex}>
        <Button
          variant="contained"
          component="label"
          fullWidth
          disabled={override.albedoMap === false}
        >
          Select Albedo map
          <input
            type="file"
            hidden
            accept=".png"
            onChange={e => {
              if (e.target.files) {
                setSwatchData({
                  ...swatchData,
                  albedoMap: e.target.files[0],
                })
              }
            }}
          />
        </Button>
        <Checkbox
          checked={override.albedoMap}
          onChange={e =>
            setOverride({ ...override, albedoMap: e.target.checked })
          }
        />
      </div>
      <Divider className={styles.margin} />
      <div className={styles.flex}>
        <Typography variant="h6" className={styles.margin}>
          Metalness
        </Typography>
        <Checkbox
          checked={override.metalness}
          onChange={e =>
            setOverride({ ...override, metalness: e.target.checked })
          }
        />
      </div>
      <Typography variant="subtitle2">Metalness Intensity</Typography>
      <Slider
        step={0.025}
        min={0}
        max={1}
        marks
        value={swatchData.metalness}
        onChange={(e: Event) =>
          setSwatchData({ ...swatchData, metalness: e.target.value })
        }
        valueLabelDisplay="auto"
        color="secondary"
        disabled={override.metalness === false}
      />
      <Divider className={styles.margin} />
      <div className={styles.flex}>
        <Typography variant="h6" className={styles.margin}>
          Roughness
        </Typography>
        <Checkbox
          checked={override.roughness}
          onChange={e =>
            setOverride({ ...override, roughness: e.target.checked })
          }
        />
      </div>
      <Typography variant="subtitle2">Roughness Intensity</Typography>
      <Slider
        step={0.025}
        min={0}
        max={1}
        marks
        value={swatchData.roughness}
        onChange={(e: Event) =>
          setSwatchData({ ...swatchData, roughness: e.target.value })
        }
        valueLabelDisplay="auto"
        color="secondary"
        disabled={!override.roughness}
      />
      <div className={styles.flex}>
        <Button
          id="roughnesMap"
          variant="contained"
          component="label"
          fullWidth
          disabled={!override.roughnessMap}
        >
          {swatchData.roughnessMap
            ? `${swatchData.roughnessMap.name}`
            : "select roughness map 'png'"}
          <input
            type="file"
            accept=".png"
            hidden
            onChange={e => {
              if (e.target.files) {
                setSwatchData({
                  ...swatchData,
                  roughnessMap: e.target.files[0],
                })
              }
            }}
          />
        </Button>
        <Checkbox
          checked={override.roughnessMap}
          onChange={e =>
            setOverride({ ...override, roughnessMap: e.target.checked })
          }
        />
      </div>
      <Divider className={styles.margin} />
      <div className={styles.flex}>
        <Typography variant="h6" className={styles.margin}>
          Normals
        </Typography>
        <Checkbox
          checked={override.normals}
          onChange={e =>
            setOverride({ ...override, normals: e.target.checked })
          }
        />
      </div>
      <Typography variant="subtitle2">Normals Intensity</Typography>
      <Slider
        step={0.1}
        min={0.1}
        max={2}
        marks
        value={swatchData.normalStrength}
        onChange={(e: Event) =>
          setSwatchData({ ...swatchData, normalStrength: e.target.value })
        }
        valueLabelDisplay="auto"
        color="secondary"
        disabled={!override.normals}
      />{' '}
      <div className={styles.cSSection}>
        <div className={styles.normalSizSli}>
          <Typography variant="subtitle2">Normal U</Typography>
          <Slider
            step={0.1}
            min={1}
            max={20}
            marks
            valueLabelDisplay="auto"
            value={swatchData.normalSizeU}
            onChange={(e: Event) =>
              setSwatchData({ ...swatchData, normalSizeU: e.target.value })
            }
            color="secondary"
            disabled={!override.normals}
          />
        </div>
        <div className={styles.normalSizSli}>
          <Typography variant="subtitle2">Normal V</Typography>
          <Slider
            step={0.1}
            min={1}
            max={20}
            marks
            valueLabelDisplay="auto"
            value={swatchData.normalSizeV}
            onChange={(e: Event) =>
              setSwatchData({ ...swatchData, normalSizeV: e.target.value })
            }
            color="secondary"
            disabled={!override.normals}
          />
        </div>
      </div>
      <div className={styles.flex}>
        <Button
          id="normalMap"
          variant="contained"
          component="label"
          fullWidth
          disabled={!override.normalMap}
        >
          {swatchData.normalMap
            ? `${swatchData.normalMap.name}`
            : "select normal map 'png'"}
          <input
            type="file"
            accept=".png"
            hidden
            onChange={e => {
              if (e.target.files) {
                setSwatchData({
                  ...swatchData,
                  normalMap: e.target.files[0],
                })
              }
            }}
          />
        </Button>
        <Checkbox
          checked={override.normalMap}
          onChange={e =>
            setOverride({ ...override, normalMap: e.target.checked })
          }
        />
      </div>
      <Divider className={styles.margin} />
      <Button
        type="submit"
        variant="contained"
        color="secondary"
        className={styles.submitBtn}
        disabled={
          swatchData.name.length === 0 ||
          swatchData.code.length === 0 ||
          swatchData.catId === undefined ||
          matType === undefined
        }
      >
        Submit
      </Button>
    </form>
  )
}

export default CreateSwatch
